import { JSX } from 'react'

export type TownCompanyAddedBy = 'school' | 'irogami'
export type TownCompanySendStatus = 'pending' | 'sent' | 'not_sending'
export type TownCompanyResponseStatus = 'accepted' | 'rejected' | 'pending'

const normalizeAddedBy = (value: string): TownCompanyAddedBy | null => {
  switch (value) {
    case 'school':
    case 'irogami':
      return value
    case '学校':
      return 'school'
    case 'イロガミ':
      return 'irogami'
    default:
      return null
  }
}

const normalizeSendStatus = (value: string): TownCompanySendStatus | null => {
  switch (value) {
    case 'pending':
    case 'sent':
    case 'not_sending':
      return value
    case '郵送前':
      return 'pending'
    case '郵送済み':
      return 'sent'
    case '郵送しない':
      return 'not_sending'
    default:
      return null
  }
}

const normalizeResponseStatus = (value: string): TownCompanyResponseStatus | null => {
  switch (value) {
    case 'accepted':
    case 'rejected':
    case 'pending':
      return value
    case '承諾':
      return 'accepted'
    case '不承諾':
      return 'rejected'
    case '未回答':
      return 'pending'
    default:
      return null
  }
}

export const parseTownCompanyAddedBy = (value: string): TownCompanyAddedBy | null => {
  return normalizeAddedBy(value)
}

export const parseTownCompanySendStatus = (value: string): TownCompanySendStatus | null => {
  return normalizeSendStatus(value)
}

export const parseTownCompanyResponseStatus = (value: string): TownCompanyResponseStatus | null => {
  return normalizeResponseStatus(value)
}

/**
 * 事業所の登録を日本語ラベルに変換
 */
export const getTownCompanyAddedByLabel = (addedBy: TownCompanyAddedBy | string): string => {
  const normalized = normalizeAddedBy(addedBy)
  switch (normalized) {
    case 'school':
      return '学校'
    case 'irogami':
      return '生成'
    default:
      return addedBy
  }
}

export type TownCompanyDeletableBySchoolRow = {
  addedBy: TownCompanyAddedBy
  sendStatus: TownCompanySendStatus
}

/**
 * 学校アプリから事業所を論理削除できるか。
 * 登録が「生成」（イロガミが追加）のデータは削除不可。管理者アプリではこの制限をかけない。
 */
export function isTownCompanyDeletableBySchool(
  item: TownCompanyDeletableBySchoolRow,
  options: { isAfterShipment: boolean; isProjectCompleted: boolean },
): boolean {
  if (item.addedBy === 'irogami') {
    return false
  }
  const isSent = item.sendStatus === 'sent'
  return !isSent && !options.isAfterShipment && !options.isProjectCompleted
}

/**
 * 事業所の郵送状況を日本語ラベルに変換
 */
export const getTownCompanySendStatusLabel = (sendStatus: TownCompanySendStatus | string): string => {
  const normalized = normalizeSendStatus(sendStatus)
  switch (normalized) {
    case 'pending':
      return '郵送前'
    case 'sent':
      return '郵送済み'
    case 'not_sending':
      return '郵送しない'
    default:
      return sendStatus
  }
}

/**
 * 事業所の回答状態を日本語ラベルに変換
 */
export const getTownCompanyResponseStatusLabel = (responseStatus: TownCompanyResponseStatus | string): string => {
  const normalized = normalizeResponseStatus(responseStatus)
  switch (normalized) {
    case 'accepted':
      return '受け入れ可能'
    case 'rejected':
      return '受け入れ不可'
    case 'pending':
      return '未回答'
    default:
      return responseStatus
  }
}

const getTownCompanyResponseStatusShortLabel = (responseStatus: TownCompanyResponseStatus | string): string => {
  const normalized = normalizeResponseStatus(responseStatus)
  switch (normalized) {
    case 'accepted':
      return '可能'
    case 'rejected':
      return '不可'
    case 'pending':
      return '未回答'
    default:
      return responseStatus
  }
}

interface TownCompanyAddedByBadgeProps {
  addedBy: TownCompanyAddedBy | string
}

/**
 * 事業所の登録バッジ
 */
export const TownCompanyAddedByBadge = ({ addedBy }: TownCompanyAddedByBadgeProps): JSX.Element => {
  const normalized = normalizeAddedBy(addedBy)
  const isSchool = normalized === 'school'
  const style = {
    backgroundColor: isSchool ? '#e3f2fd' : '#fff3e0',
    color: isSchool ? '#1976d2' : '#e65100',
  }

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      {getTownCompanyAddedByLabel(addedBy)}
    </span>
  )
}

interface TownCompanySendStatusBadgeProps {
  sendStatus: TownCompanySendStatus | string
}

/**
 * 事業所の郵送状況バッジ
 */
export const TownCompanySendStatusBadge = ({
  sendStatus,
}: TownCompanySendStatusBadgeProps): JSX.Element => {
  const normalized = normalizeSendStatus(sendStatus)
  const getStatusStyle = () => {
    switch (normalized) {
      case 'sent':
        return {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
        }
      case 'pending':
        return {
          backgroundColor: '#fff3e0',
          color: '#e65100',
        }
      case 'not_sending':
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        }
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        }
    }
  }

  const style = getStatusStyle()

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      {getTownCompanySendStatusLabel(sendStatus)}
    </span>
  )
}

interface TownCompanyResponseStatusBadgeProps {
  responseStatus: TownCompanyResponseStatus | string
}

/**
 * 事業所の回答状態バッジ
 */
export const TownCompanyResponseStatusBadge = ({
  responseStatus,
}: TownCompanyResponseStatusBadgeProps): JSX.Element => {
  const normalized = normalizeResponseStatus(responseStatus)
  const getStatusStyle = () => {
    switch (normalized) {
      case 'accepted':
        return {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
        }
      case 'rejected':
        return {
          backgroundColor: '#ffebee',
          color: '#c62828',
        }
      case 'pending':
        return {
          backgroundColor: '#fff3e0',
          color: '#e65100',
        }
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        }
    }
  }

  const style = getStatusStyle()

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      {getTownCompanyResponseStatusShortLabel(responseStatus)}
    </span>
  )
}

